import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import api, { API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CertUploadDialog } from "@/components/CertUploadDialog";
import { DownloadSimple, WarningCircle, CheckCircle, Clock, UploadSimple, FilePdf } from "@phosphor-icons/react";

/* ============================================================
 * Admin → Reports
 *
 *   /admin/reports  (tabs)
 *     • LOA           → members currently on Leave of Absence
 *     • Hour Compliance → required vs completed vs remaining hours
 *     • Paperwork Compliance → per-member required-doc scoreboard
 *
 * Each tab is a thin renderer over the corresponding server report.
 * CSV export is a client-side dump so admins can drop it into Excel.
 * ========================================================== */

function fmtDate(d) {
  if (!d) return "—";
  try {
    return new Date(d + (d.length === 10 ? "T12:00:00" : "")).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch { return d; }
}
function toCSV(headers, rows) {
  const esc = (v) => {
    if (v == null) return "";
    const s = String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return [headers.join(","), ...rows.map((r) => r.map(esc).join(","))].join("\n");
}
function downloadCSV(name, csv) {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = name; a.click();
  URL.revokeObjectURL(url);
}

/* ============ LOA Report ============ */
function LOATab() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [historyMember, setHistoryMember] = useState(null);
  useEffect(() => {
    api.get("/reports/loa").then((r) => setData(r.data)).catch((e) => toast.error(e.response?.data?.detail || "Failed"));
  }, []);
  const rows = useMemo(() => {
    if (!data) return [];
    const needle = q.trim().toLowerCase();
    return needle
      ? data.rows.filter((r) => (`${r.name} ${r.agency} ${r.role} ${(r.certifications || []).join(" ")}`).toLowerCase().includes(needle))
      : data.rows;
  }, [data, q]);
  function exportCsv() {
    const headers = ["Member", "Agency", "Position", "Certifications", "LOA Start", "LOA End", "Duration (days)", "Reason", "Status"];
    const body = rows.map((r) => [r.name, r.agency, r.role, (r.certifications || []).join("; "), r.loa_start, r.loa_end, r.loa_duration_days, r.loa_reason, r.status]);
    downloadCSV(`bsvac-loa-${new Date().toISOString().slice(0, 10)}.csv`, toCSV(headers, body));
  }
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-xs text-slate-500">{data ? `${data.count} member${data.count === 1 ? "" : "s"} on Leave of Absence` : "Loading…"}</p>
        <div className="flex items-center gap-2">
          <Input placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} className="rounded-sm w-56" data-testid="loa-search" />
          <Button size="sm" variant="outline" className="rounded-sm" onClick={exportCsv} disabled={!rows.length} data-testid="loa-export">
            <DownloadSimple size={12} weight="bold" className="mr-1" /> CSV
          </Button>
          <Button asChild size="sm" className="rounded-sm bg-slate-900 text-white" data-testid="loa-pdf">
            <a href={`${API_BASE}/reports/loa/pdf`} target="_blank" rel="noreferrer">
              <FilePdf size={12} weight="bold" className="mr-1" /> PDF
            </a>
          </Button>
        </div>
      </div>
      <div className="surface-grid overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-widest text-slate-500">
            <tr>
              <th className="px-4 py-2 text-left">Member</th>
              <th className="px-4 py-2 text-left">Agency</th>
              <th className="px-4 py-2 text-left">Position</th>
              <th className="px-4 py-2 text-left">LOA Start</th>
              <th className="px-4 py-2 text-left">LOA End</th>
              <th className="px-4 py-2 text-right">Duration</th>
              <th className="px-4 py-2 text-left">Reason</th>
              <th className="px-4 py-2 text-left">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={8} className="px-4 py-6 text-center text-slate-500">No members on LOA.</td></tr>
            ) : rows.map((r) => (
              <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`loa-row-${r.id}`}>
                <td className="px-4 py-2 font-medium">
                  <button
                    type="button"
                    onClick={() => setHistoryMember({ id: r.id, name: r.name })}
                    className="text-slate-900 hover:text-blue-700 hover:underline"
                    data-testid={`loa-history-${r.id}`}
                  >
                    {r.name}
                  </button>
                </td>
                <td className="px-4 py-2 mono text-xs">{r.agency}</td>
                <td className="px-4 py-2"><Badge variant="outline" className="rounded-sm text-[10px]">{r.role}</Badge></td>
                <td className="px-4 py-2 mono text-xs">{fmtDate(r.loa_start)}</td>
                <td className="px-4 py-2 mono text-xs">{fmtDate(r.loa_end)}</td>
                <td className="px-4 py-2 text-right mono">{r.loa_duration_days ?? "—"}d</td>
                <td className="px-4 py-2 text-xs truncate max-w-[260px]">{r.loa_reason || "—"}</td>
                <td className="px-4 py-2">
                  <Badge className={`rounded-sm text-[10px] ${r.status === "Overdue" ? "bg-red-600 text-white" : r.status.startsWith("Active") ? "bg-amber-500 text-white" : "bg-slate-700 text-white"}`}>{r.status}</Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <LOAHistoryDialog member={historyMember} onClose={() => setHistoryMember(null)} />
    </div>
  );
}

/* --------- LOA History dialog ---------
   Opens when an admin clicks a member's name in the LOA report. Pulls
   current + past LOA periods from the API and offers a per-member
   Download PDF. */
function LOAHistoryDialog({ member, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!member) { setData(null); return; }
    api.get(`/reports/loa/history/${member.id}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(e.response?.data?.detail || "Failed to load history"));
  }, [member]);
  return (
    <Dialog open={!!member} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="loa-history-dialog">
        <DialogHeader className="pb-3 border-b border-slate-200">
          <DialogTitle className="font-display text-xl">Leave of Absence History · {member?.name}</DialogTitle>
          <p className="text-xs text-slate-500 mt-1">Full history of prior and current Leave of Absence periods.</p>
        </DialogHeader>
        <div className="pt-4 space-y-3">
          {!data ? (
            <div className="text-sm text-slate-500 text-center py-6">Loading history…</div>
          ) : data.history.length === 0 ? (
            <div className="text-sm text-slate-500 italic text-center py-6">No Leave of Absence history on record.</div>
          ) : (
            <div className="surface-grid overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-widest text-slate-500">
                  <tr>
                    <th className="px-4 py-2 text-left">Start</th>
                    <th className="px-4 py-2 text-left">End</th>
                    <th className="px-4 py-2 text-right">Duration</th>
                    <th className="px-4 py-2 text-left">Reason</th>
                    <th className="px-4 py-2 text-left">Granted By</th>
                    <th className="px-4 py-2 text-left">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.history.map((h, i) => (
                    <tr key={i} className="border-b border-slate-100" data-testid={`loa-hist-row-${i}`}>
                      <td className="px-4 py-2 mono text-xs">{fmtDate(h.start_date)}</td>
                      <td className="px-4 py-2 mono text-xs">{fmtDate(h.end_date)}</td>
                      <td className="px-4 py-2 text-right mono text-xs">{h.duration_days ? `${h.duration_days}d` : "—"}</td>
                      <td className="px-4 py-2 text-xs truncate max-w-[260px]">{h.reason || "—"}</td>
                      <td className="px-4 py-2 text-xs">{h.granted_by_name || "—"}</td>
                      <td className="px-4 py-2">
                        <Badge className={`rounded-sm text-[10px] ${h.status === "Active" ? "bg-amber-500 text-white" : h.status === "Overdue" ? "bg-red-600 text-white" : "bg-slate-700 text-white"}`}>{h.status}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <DialogFooter className="pt-3 border-t border-slate-200">
          {member && (
            <Button asChild className="rounded-sm bg-slate-900 text-white" data-testid="loa-hist-pdf">
              <a href={`${API_BASE}/reports/loa/history/${member.id}/pdf`} target="_blank" rel="noreferrer">
                <FilePdf size={12} weight="bold" className="mr-1" /> Download PDF
              </a>
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ============ Hour Compliance Report ============ */
function HourTab() {
  const [data, setData] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [q, setQ] = useState("");
  const [only, setOnly] = useState("all");

  async function load() {
    try {
      const r = await api.get(`/reports/hour-compliance?year=${year}&month=${month}`);
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  }
  useEffect(() => { load(); }, [year, month]);

  const rows = useMemo(() => {
    if (!data) return [];
    let out = data.rows;
    if (only === "non") out = out.filter((r) => r.compliance_status === "Non-Compliant");
    else if (only === "compliant") out = out.filter((r) => r.compliance_status === "Compliant");
    else if (only === "loa") out = out.filter((r) => r.compliance_status === "LOA");
    const needle = q.trim().toLowerCase();
    if (needle) out = out.filter((r) => (`${r.name} ${r.agency} ${r.role} ${(r.certifications || []).join(" ")}`).toLowerCase().includes(needle));
    return out;
  }, [data, q, only]);

  const stats = useMemo(() => {
    if (!data) return null;
    let compliant = 0, non = 0, loa = 0;
    data.rows.forEach((r) => {
      if (r.compliance_status === "Compliant") compliant++;
      else if (r.compliance_status === "Non-Compliant") non++;
      else if (r.compliance_status === "LOA") loa++;
    });
    return { compliant, non, loa, total: data.rows.length };
  }, [data]);

  function exportCsv() {
    const headers = ["Member", "Agency", "Role", "Certifications", "Required Hours", "Hours Completed", "Hours Remaining", "Compliance Status"];
    const body = rows.map((r) => [r.name, r.agency, r.role, (r.certifications || []).join("; "), r.required_hours, r.hours_completed, r.hours_remaining, r.compliance_status]);
    downloadCSV(`bsvac-hours-${year}-${String(month).padStart(2, "0")}.csv`, toCSV(headers, body));
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <select value={month} onChange={(e) => setMonth(+e.target.value)} className="border border-slate-200 rounded-sm h-9 px-2 text-xs bg-white" data-testid="hour-month">
            {["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"].map((m,i) => <option key={i} value={i+1}>{m}</option>)}
          </select>
          <select value={year} onChange={(e) => setYear(+e.target.value)} className="border border-slate-200 rounded-sm h-9 px-2 text-xs bg-white mono" data-testid="hour-year">
            {[year - 2, year - 1, year, year + 1].map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <select value={only} onChange={(e) => setOnly(e.target.value)} className="border border-slate-200 rounded-sm h-9 px-2 text-xs bg-white" data-testid="hour-filter">
            <option value="all">All members</option>
            <option value="non">Non-Compliant only</option>
            <option value="compliant">Compliant only</option>
            <option value="loa">On LOA only</option>
          </select>
        </div>
        <div className="flex items-center gap-2">
          <Input placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} className="rounded-sm w-56" data-testid="hour-search" />
          <Button size="sm" variant="outline" className="rounded-sm" onClick={exportCsv} disabled={!rows.length} data-testid="hour-export">
            <DownloadSimple size={12} weight="bold" className="mr-1" /> CSV
          </Button>
          <Button asChild size="sm" className="rounded-sm bg-slate-900 text-white" data-testid="hour-pdf">
            <a href={`${API_BASE}/reports/hour-compliance/pdf?year=${year}&month=${month}`} target="_blank" rel="noreferrer">
              <FilePdf size={12} weight="bold" className="mr-1" /> PDF
            </a>
          </Button>
        </div>
      </div>
      {stats && (
        <div className="flex flex-wrap gap-2 text-xs">
          <Badge className="rounded-sm bg-slate-900 text-white" data-testid="hour-stat-total">{stats.total} total</Badge>
          <Badge className="rounded-sm bg-emerald-600 text-white" data-testid="hour-stat-compliant">{stats.compliant} compliant</Badge>
          <Badge className="rounded-sm bg-red-600 text-white" data-testid="hour-stat-non">{stats.non} non-compliant</Badge>
          <Badge className="rounded-sm bg-amber-500 text-white" data-testid="hour-stat-loa">{stats.loa} on LOA</Badge>
          <span className="text-slate-500 ml-2">threshold: {data.threshold}h/month</span>
        </div>
      )}
      <div className="surface-grid overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-widest text-slate-500">
            <tr>
              <th className="px-4 py-2 text-left">Member</th>
              <th className="px-4 py-2 text-left">Agency</th>
              <th className="px-4 py-2 text-left">Role</th>
              <th className="px-4 py-2 text-right">Required</th>
              <th className="px-4 py-2 text-right">Completed</th>
              <th className="px-4 py-2 text-right">Remaining</th>
              <th className="px-4 py-2 text-left">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-6 text-center text-slate-500">No members match.</td></tr>
            ) : rows.map((r) => {
              const pct = r.required_hours ? Math.min(100, Math.round((r.hours_completed / r.required_hours) * 100)) : 0;
              return (
                <tr key={r.id} className={`border-b border-slate-100 ${r.compliance_status === "Non-Compliant" ? "bg-red-50" : ""}`} data-testid={`hour-row-${r.id}`}>
                  <td className="px-4 py-2 font-medium"><Link to={`/members/${r.id}`} className="hover:text-blue-700 hover:underline">{r.name}</Link></td>
                  <td className="px-4 py-2 mono text-xs">{r.agency}</td>
                  <td className="px-4 py-2"><Badge variant="outline" className="rounded-sm text-[10px]">{r.role}</Badge></td>
                  <td className="px-4 py-2 text-right mono">{r.required_hours}</td>
                  <td className="px-4 py-2 text-right mono">
                    {r.hours_completed}
                    <div className="mt-1 h-1 rounded-sm bg-slate-200 overflow-hidden w-16 ml-auto">
                      <div className={`h-full ${pct >= 100 ? "bg-emerald-500" : "bg-amber-500"}`} style={{ width: `${pct}%` }} />
                    </div>
                  </td>
                  <td className={`px-4 py-2 text-right mono ${r.hours_remaining > 0 ? "text-red-700 font-bold" : "text-emerald-700"}`}>{r.hours_remaining}</td>
                  <td className="px-4 py-2">
                    <Badge className={`rounded-sm text-[10px] ${r.compliance_status === "Compliant" ? "bg-emerald-600 text-white" : r.compliance_status === "LOA" ? "bg-amber-500 text-white" : "bg-red-600 text-white"}`}>{r.compliance_status.toUpperCase()}</Badge>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ============ Paperwork Compliance Report ============ */
const STATE_ICON = { approved: CheckCircle, pending: Clock, expired: WarningCircle, missing: WarningCircle };
const STATE_STYLE = {
  approved: "bg-emerald-100 text-emerald-800 border-emerald-300",
  pending: "bg-amber-100 text-amber-800 border-amber-300",
  expired: "bg-red-100 text-red-800 border-red-300",
  rejected: "bg-red-100 text-red-800 border-red-300",
  missing: "bg-slate-100 text-slate-700 border-slate-300",
};
function PaperworkTab() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [only, setOnly] = useState("all");
  const [expanded, setExpanded] = useState({});
  const [uploadFor, setUploadFor] = useState(null); // { member, category }

  async function load() {
    try {
      const r = await api.get("/reports/paperwork-compliance");
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  }
  useEffect(() => { load(); }, []);

  const rows = useMemo(() => {
    if (!data) return [];
    let out = data.rows;
    if (only === "non") out = out.filter((r) => r.compliance_status === "Non-Compliant");
    else if (only === "compliant") out = out.filter((r) => r.compliance_status === "Compliant");
    else if (only === "expired") out = out.filter((r) => r.expired_count > 0);
    else if (only === "pending") out = out.filter((r) => r.pending_count > 0);
    const needle = q.trim().toLowerCase();
    if (needle) out = out.filter((r) => (`${r.name} ${r.agency} ${r.role} ${(r.certifications || []).join(" ")}`).toLowerCase().includes(needle));
    return out;
  }, [data, q, only]);

  function exportCsv() {
    const headers = ["Member", "Agency", "Role", "Required Doc", "State", "Filename", "Expiration", "Approved By", "Approved At"];
    const body = [];
    rows.forEach((r) => {
      r.items.forEach((it) => body.push([r.name, r.agency, r.role, it.category, it.state, it.filename || "", it.expiration_date || "", it.approved_by || "", it.approved_at || ""]));
    });
    downloadCSV(`bsvac-paperwork-${new Date().toISOString().slice(0, 10)}.csv`, toCSV(headers, body));
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <select value={only} onChange={(e) => setOnly(e.target.value)} className="border border-slate-200 rounded-sm h-9 px-2 text-xs bg-white" data-testid="pw-filter">
            <option value="all">All members</option>
            <option value="non">Non-Compliant only</option>
            <option value="compliant">Compliant only</option>
            <option value="expired">Any Expired</option>
            <option value="pending">Any Pending</option>
          </select>
        </div>
        <div className="flex items-center gap-2">
          <Input placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} className="rounded-sm w-56" data-testid="pw-search" />
          <Button size="sm" variant="outline" className="rounded-sm" onClick={exportCsv} disabled={!rows.length} data-testid="pw-export">
            <DownloadSimple size={12} weight="bold" className="mr-1" /> CSV
          </Button>
          <Button asChild size="sm" className="rounded-sm bg-slate-900 text-white" data-testid="pw-pdf">
            <a href={`${API_BASE}/reports/paperwork-compliance/pdf`} target="_blank" rel="noreferrer">
              <FilePdf size={12} weight="bold" className="mr-1" /> PDF
            </a>
          </Button>
        </div>
      </div>
      <div className="surface-grid overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-widest text-slate-500">
            <tr>
              <th className="px-4 py-2 text-left">Member</th>
              <th className="px-4 py-2 text-left">Agency</th>
              <th className="px-4 py-2 text-right">Approved</th>
              <th className="px-4 py-2 text-right">Pending</th>
              <th className="px-4 py-2 text-right">Expired</th>
              <th className="px-4 py-2 text-right">Missing</th>
              <th className="px-4 py-2 text-left">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-6 text-center text-slate-500">No members match.</td></tr>
            ) : rows.map((r) => (
              <React.Fragment key={r.id}>
                <tr
                  className={`border-b border-slate-100 hover:bg-slate-50 cursor-pointer ${r.compliance_status === "Non-Compliant" ? "bg-red-50" : ""}`}
                  onClick={() => setExpanded((e) => ({ ...e, [r.id]: !e[r.id] }))}
                  data-testid={`pw-row-${r.id}`}
                >
                  <td className="px-4 py-2 font-medium">
                    <span className="mr-1 text-slate-400">{expanded[r.id] ? "▾" : "▸"}</span>
                    <Link to={`/members/${r.id}`} onClick={(e) => e.stopPropagation()} className="hover:text-blue-700 hover:underline">{r.name}</Link>
                    <div className="text-[10px] text-slate-500">{(r.certifications || []).join(", ") || "—"}</div>
                  </td>
                  <td className="px-4 py-2 mono text-xs">{r.agency}</td>
                  <td className="px-4 py-2 text-right mono text-emerald-700">{r.approved_count}</td>
                  <td className="px-4 py-2 text-right mono text-amber-700">{r.pending_count}</td>
                  <td className="px-4 py-2 text-right mono text-red-700">{r.expired_count}</td>
                  <td className="px-4 py-2 text-right mono text-slate-600">{r.missing_count}</td>
                  <td className="px-4 py-2">
                    <Badge className={`rounded-sm text-[10px] ${r.compliance_status === "Compliant" ? "bg-emerald-600 text-white" : r.compliance_status === "N/A" ? "bg-slate-500 text-white" : "bg-red-600 text-white"}`}>{r.compliance_status.toUpperCase()}</Badge>
                  </td>
                </tr>
                {expanded[r.id] && (
                  <tr className="bg-slate-50">
                    <td colSpan={7} className="px-4 py-3">
                      {r.items.length === 0 ? (
                        <div className="text-xs text-slate-500 italic">No documents required for this member's certifications.</div>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {r.items.map((it, i) => {
                            const Icon = STATE_ICON[it.state] || Clock;
                            return (
                              <div key={i} className="flex items-center justify-between border border-slate-200 rounded-sm bg-white px-2 py-1.5 gap-2" data-testid={`pw-item-${r.id}-${i}`}>
                                <div className="min-w-0 flex-1">
                                  <div className="font-medium text-sm truncate">{it.category}</div>
                                  <div className="text-[10px] text-slate-500 mono">
                                    {it.filename ? `${it.filename}` : "—"}
                                    {it.expiration_date ? ` · exp ${it.expiration_date}` : ""}
                                    {it.certification_number ? ` · cert #${it.certification_number}` : ""}
                                    {it.dl_number ? ` · DL ${it.dl_state || ""}-${it.dl_number}` : ""}
                                    {it.approved_by ? ` · by ${it.approved_by}` : ""}
                                  </div>
                                </div>
                                <div className="flex items-center gap-1.5 shrink-0">
                                  <Badge className={`rounded-sm border inline-flex items-center gap-1 ${STATE_STYLE[it.state] || STATE_STYLE.pending}`}>
                                    <Icon size={10} weight="fill" />
                                    {it.state.toUpperCase()}
                                  </Badge>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="rounded-sm h-7 text-xs"
                                    onClick={(e) => { e.stopPropagation(); setUploadFor({ member: r, category: it.category }); }}
                                    data-testid={`pw-upload-${r.id}-${i}`}
                                  >
                                    <UploadSimple size={11} weight="bold" className="mr-1" /> Upload
                                  </Button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      <CertUploadDialog open={!!uploadFor} category={uploadFor?.category} targetUser={uploadFor?.member} onClose={() => setUploadFor(null)} onDone={load} />
    </div>
  );
}

/* ============ Drop Tours Report ============ */
export function DropToursTab() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");

  async function load() {
    try {
      const params = new URLSearchParams();
      if (status && status !== "all") params.set("status", status);
      if (start) params.set("start", `${start}T00:00:00`);
      if (end) params.set("end", `${end}T23:59:59`);
      const r = await api.get(`/reports/drop-tours?${params.toString()}`);
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, start, end]);

  const rows = useMemo(() => {
    if (!data) return [];
    const needle = q.trim().toLowerCase();
    if (!needle) return data.rows;
    return data.rows.filter((r) =>
      `${r.volunteer_name || ""} ${r.position || ""} ${r.reason || ""} ${r.decided_by_name || ""}`
        .toLowerCase().includes(needle)
    );
  }, [data, q]);

  function exportCsv() {
    const headers = ["Volunteer", "Tour Date", "Position", "Ambulance Role", "Requested At", "Reason", "Hours Until Start", "Status", "Decided By", "Decided At"];
    const body = rows.map((r) => [
      r.volunteer_name || "", (r.tour_start || "").slice(0, 10), r.position || "", r.ambulance_role || "",
      r.requested_at || "", r.reason || "", r.hours_until_start ?? "",
      r.status || "", r.decided_by_name || "", r.decided_at || "",
    ]);
    downloadCSV(`bsvac-drop-tours-${new Date().toISOString().slice(0, 10)}.csv`, toCSV(headers, body));
  }

  const params = new URLSearchParams();
  if (status && status !== "all") params.set("status", status);
  if (start) params.set("start", `${start}T00:00:00`);
  if (end) params.set("end", `${end}T23:59:59`);
  const pdfUrl = `${API_BASE}/reports/drop-tours/pdf?${params.toString()}`;

  const statusBadge = (s) => {
    const cls = s === "approved" ? "bg-emerald-600 text-white"
      : s === "denied" ? "bg-red-600 text-white"
      : s === "auto_released" ? "bg-blue-600 text-white"
      : "bg-amber-500 text-white";
    return <Badge className={`rounded-sm text-[10px] ${cls}`}>{(s || "pending").replace("_", " ").toUpperCase()}</Badge>;
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="border border-slate-200 rounded-sm h-9 px-2 text-xs bg-white" data-testid="drop-filter-status">
            <option value="all">All statuses</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="denied">Denied</option>
            <option value="auto_released">Auto-Released</option>
          </select>
          <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="rounded-sm w-40" data-testid="drop-start" title="Requested on/after" />
          <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="rounded-sm w-40" data-testid="drop-end" title="Requested on/before" />
        </div>
        <div className="flex items-center gap-2">
          <Input placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} className="rounded-sm w-56" data-testid="drop-search" />
          <Button size="sm" variant="outline" className="rounded-sm" onClick={exportCsv} disabled={!rows.length} data-testid="drop-export">
            <DownloadSimple size={12} weight="bold" className="mr-1" /> CSV
          </Button>
          <Button asChild size="sm" className="rounded-sm bg-slate-900 text-white" data-testid="drop-pdf">
            <a href={pdfUrl} target="_blank" rel="noreferrer">
              <FilePdf size={12} weight="bold" className="mr-1" /> PDF
            </a>
          </Button>
        </div>
      </div>
      {data && data.pattern_watch && data.pattern_watch.flagged_count > 0 && (
        <div className="surface-grid border-amber-300 bg-amber-50 p-3" data-testid="drop-pattern-watch">
          <div className="flex items-center gap-2 mb-2">
            <WarningCircle size={16} weight="fill" className="text-amber-700" />
            <div className="font-display font-bold text-amber-900 text-sm uppercase tracking-wide">
              Pattern Watch — {data.pattern_watch.flagged_count} member{data.pattern_watch.flagged_count === 1 ? "" : "s"} with {data.pattern_watch.threshold}+ drops in {data.pattern_watch.window_days} days
            </div>
          </div>
          <p className="text-[11px] text-amber-800 mb-2">Consider an early check-in with these members. Rows for flagged volunteers are highlighted amber below.</p>
          <div className="flex flex-wrap gap-2">
            {data.pattern_watch.flagged.map((f) => (
              <div
                key={f.volunteer_id}
                className="inline-flex items-center gap-2 border border-amber-300 bg-white rounded-sm px-2 py-1 text-xs"
                data-testid={`drop-flag-${f.volunteer_id}`}
              >
                <Badge className="rounded-sm bg-amber-500 text-white text-[10px]">{f.drop_count_30d}</Badge>
                <span className="font-medium">{f.volunteer_name}</span>
                <span className="text-[10px] text-slate-500 mono">last: {(f.last_drop_at || "").slice(0, 10)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {data && (
        <div className="flex flex-wrap gap-2 text-xs" data-testid="drop-stats">
          <Badge className="rounded-sm bg-slate-900 text-white">{data.total} total</Badge>
          <Badge className="rounded-sm bg-amber-500 text-white">{data.counts?.pending || 0} pending</Badge>
          <Badge className="rounded-sm bg-emerald-600 text-white">{data.counts?.approved || 0} approved</Badge>
          <Badge className="rounded-sm bg-red-600 text-white">{data.counts?.denied || 0} denied</Badge>
          <Badge className="rounded-sm bg-blue-600 text-white">{data.counts?.auto_released || 0} auto-released</Badge>
        </div>
      )}
      <div className="surface-grid overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-widest text-slate-500">
            <tr>
              <th className="px-4 py-2 text-left">Volunteer</th>
              <th className="px-4 py-2 text-left">Tour Date</th>
              <th className="px-4 py-2 text-left">Position</th>
              <th className="px-4 py-2 text-left">Requested</th>
              <th className="px-4 py-2 text-left">Reason</th>
              <th className="px-4 py-2 text-left">Status</th>
              <th className="px-4 py-2 text-left">Decided By</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-6 text-center text-slate-500">No drop requests match this filter.</td></tr>
            ) : rows.map((r, i) => (
              <tr
                key={`${r.tour_id}-${r.volunteer_id}-${i}`}
                className={`border-b border-slate-100 hover:bg-slate-50 ${r.pattern_flagged ? "bg-amber-50" : ""}`}
                data-testid={`drop-row-${i}`}
              >
                <td className="px-4 py-2 font-medium">
                  {r.pattern_flagged && (
                    <span className="mr-1.5 inline-flex items-center gap-1 text-amber-700" title="3+ drops in the last 30 days">
                      <WarningCircle size={12} weight="fill" />
                    </span>
                  )}
                  {r.volunteer_name || "—"}
                  {r.pattern_flagged && <Badge className="ml-2 rounded-sm bg-amber-500 text-white text-[9px]">PATTERN</Badge>}
                </td>
                <td className="px-4 py-2 mono text-xs">{r.tour_start ? new Date(r.tour_start).toLocaleDateString() : "—"}</td>
                <td className="px-4 py-2 text-xs">
                  <div>{r.position || "—"}</div>
                  {r.ambulance_role && <div className="text-[10px] text-slate-500">{r.ambulance_role}</div>}
                </td>
                <td className="px-4 py-2 mono text-xs">
                  {r.requested_at ? new Date(r.requested_at).toLocaleString() : "—"}
                  {r.hours_until_start != null && (
                    <div className="text-[10px] text-slate-500">{r.hours_until_start}h before start</div>
                  )}
                </td>
                <td className="px-4 py-2 text-xs truncate max-w-[280px]">{r.reason || "—"}</td>
                <td className="px-4 py-2">{statusBadge(r.status)}</td>
                <td className="px-4 py-2 text-xs">
                  {r.decided_by_name || (r.status === "auto_released" ? <span className="text-slate-400 italic">Auto (&gt;24h)</span> : "—")}
                  {r.decided_at && <div className="text-[10px] text-slate-500 mono">{new Date(r.decided_at).toLocaleString()}</div>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function AdminReports() {
  return (
    <AppLayout title="Admin Reports" subtitle="Leave of Absence · Hour Compliance · Paperwork Compliance · Training Completion · Clearance · Dispatcher">
      <Tabs defaultValue="loa" className="w-full">
        <TabsList className="rounded-sm bg-slate-100 mb-4 flex-wrap h-auto">
          <TabsTrigger value="loa" data-testid="tab-loa" className="rounded-sm">LOA Report</TabsTrigger>
          <TabsTrigger value="hours" data-testid="tab-hours" className="rounded-sm">Hour Compliance</TabsTrigger>
          <TabsTrigger value="paperwork" data-testid="tab-pw" className="rounded-sm">Paperwork Compliance</TabsTrigger>
          <TabsTrigger value="training" data-testid="tab-training" className="rounded-sm">Training Completion</TabsTrigger>
          <TabsTrigger value="clearance" data-testid="tab-clearance" className="rounded-sm">Clearance Status</TabsTrigger>
          <TabsTrigger value="dispatcher" data-testid="tab-dispatcher" className="rounded-sm">Dispatcher Clearance</TabsTrigger>
        </TabsList>
        <TabsContent value="loa"><LOATab /></TabsContent>
        <TabsContent value="hours"><HourTab /></TabsContent>
        <TabsContent value="paperwork"><PaperworkTab /></TabsContent>
        <TabsContent value="training"><TrainingCompletionTab /></TabsContent>
        <TabsContent value="clearance"><ClearanceStatusTab /></TabsContent>
        <TabsContent value="dispatcher"><DispatcherClearanceTab /></TabsContent>
      </Tabs>
    </AppLayout>
  );
}

/* ============ Training Completion Report ============ */
function TrainingCompletionTab() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [only, setOnly] = useState("all"); // all | complete | incomplete
  const [pickedMember, setPickedMember] = useState("");

  async function load() {
    try {
      const params = pickedMember ? `?user_id=${encodeURIComponent(pickedMember)}` : "";
      const r = await api.get(`/reports/training-completion${params}`);
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [pickedMember]);

  const rows = useMemo(() => {
    if (!data) return [];
    let out = data.rows;
    if (only === "complete") out = out.filter((r) => r.required_count > 0 && r.completed_count === r.required_count);
    else if (only === "incomplete") out = out.filter((r) => r.required_count === 0 || r.completed_count < r.required_count);
    const needle = q.trim().toLowerCase();
    if (needle) out = out.filter((r) => (`${r.name} ${(r.certifications || []).join(" ")}`).toLowerCase().includes(needle));
    return out;
  }, [data, q, only]);

  const pdfUrl = pickedMember
    ? `${API_BASE}/reports/training-completion/pdf?user_id=${encodeURIComponent(pickedMember)}`
    : `${API_BASE}/reports/training-completion/pdf`;

  const [expanded, setExpanded] = useState({});
  const allMembers = data?.rows || [];

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={pickedMember}
            onChange={(e) => setPickedMember(e.target.value)}
            className="border border-slate-200 rounded-sm h-9 px-2 text-xs bg-white min-w-[220px]"
            data-testid="training-member-picker"
          >
            <option value="">— All members —</option>
            {[...allMembers].sort((a, b) => (a.name || "").localeCompare(b.name || "")).map((m) => (
              <option key={m.id} value={m.id}>{m.name}</option>
            ))}
          </select>
          <select value={only} onChange={(e) => setOnly(e.target.value)} className="border border-slate-200 rounded-sm h-9 px-2 text-xs bg-white" data-testid="training-filter">
            <option value="all">All</option>
            <option value="complete">Complete only</option>
            <option value="incomplete">Incomplete only</option>
          </select>
        </div>
        <div className="flex items-center gap-2">
          <Input placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} className="rounded-sm w-56" data-testid="training-search" />
          <Button asChild size="sm" className="rounded-sm bg-slate-900 text-white" data-testid="training-pdf">
            <a href={pdfUrl} target="_blank" rel="noreferrer">
              <FilePdf size={12} weight="bold" className="mr-1" /> PDF
            </a>
          </Button>
        </div>
      </div>
      <div className="surface-grid overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-widest text-slate-500">
            <tr>
              <th className="px-4 py-2 text-left">Member</th>
              <th className="px-4 py-2 text-left">Classification</th>
              <th className="px-4 py-2 text-right">Completed</th>
              <th className="px-4 py-2 text-right">Required</th>
              <th className="px-4 py-2 text-right">%</th>
              <th className="px-4 py-2 text-left">Outstanding</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-6 text-center text-slate-500">No members match.</td></tr>
            ) : rows.map((r) => {
              const isComplete = r.required_count > 0 && r.completed_count === r.required_count;
              return (
                <React.Fragment key={r.id}>
                  <tr
                    className={`border-b border-slate-100 hover:bg-slate-50 cursor-pointer ${isComplete ? "bg-emerald-50" : (r.completed_count === 0 && r.required_count > 0 ? "bg-red-50" : "")}`}
                    onClick={() => setExpanded((e) => ({ ...e, [r.id]: !e[r.id] }))}
                    data-testid={`training-row-${r.id}`}
                  >
                    <td className="px-4 py-2 font-medium">
                      <span className="mr-1 text-slate-400">{expanded[r.id] ? "▾" : "▸"}</span>
                      <Link to={`/members/${r.id}`} onClick={(e) => e.stopPropagation()} className="hover:text-blue-700 hover:underline">{r.name}</Link>
                    </td>
                    <td className="px-4 py-2 text-xs">{r.classification_summary}</td>
                    <td className="px-4 py-2 text-right mono">{r.completed_count}</td>
                    <td className="px-4 py-2 text-right mono">{r.required_count}</td>
                    <td className="px-4 py-2 text-right">
                      <Badge className={`rounded-sm text-[10px] ${isComplete ? "bg-emerald-600 text-white" : (r.percent_complete >= 50 ? "bg-amber-500 text-white" : "bg-red-600 text-white")}`}>
                        {r.percent_complete}%
                      </Badge>
                    </td>
                    <td className="px-4 py-2 text-[11px] truncate max-w-[280px] text-red-700">
                      {r.outstanding && r.outstanding.length ? r.outstanding.join(", ") : <span className="text-slate-400 italic">—</span>}
                    </td>
                  </tr>
                  {expanded[r.id] && (
                    <tr className="bg-slate-50">
                      <td colSpan={6} className="px-4 py-3">
                        {r.classes.length === 0 ? (
                          <div className="text-xs text-slate-500 italic">No required classes.</div>
                        ) : (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            {r.classes.map((c) => (
                              <div key={c.class_id} className={`flex items-center justify-between border rounded-sm px-2 py-1.5 bg-white text-xs ${c.completed ? "border-emerald-200" : "border-red-200 bg-red-50"}`} data-testid={`training-class-${r.id}-${c.class_id}`}>
                                <span className="font-medium">{c.class_name}</span>
                                <span className={`mono ${c.completed ? "text-emerald-700" : "text-red-700 font-bold"}`}>
                                  {c.completed ? fmtDate(c.date) : "OUTSTANDING"}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ============ Clearance Status Report ============ */
const CLEARANCE_CODES = ["N", "I", "C", "C+", "FTO"];
const CLASSIFICATIONS = ["EMT", "EMT Intern", "Paramedic", "Paramedic Intern", "Intern", "MVO", "Student"];
function ClearanceStatusTab() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState([]);
  const [classFilter, setClassFilter] = useState([]);

  async function load() {
    try {
      const params = {};
      if (statusFilter.length) params.status = statusFilter.join(",");
      if (classFilter.length) params.classification = classFilter.join(",");
      const r = await api.get("/reports/clearance-status", { params });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load");
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter, classFilter]);

  const rows = useMemo(() => {
    if (!data) return [];
    const needle = q.trim().toLowerCase();
    return needle
      ? data.rows.filter((r) => (`${r.name} ${r.member_id} ${r.classification} ${r.clearance_status}`).toLowerCase().includes(needle))
      : data.rows;
  }, [data, q]);

  function toggle(arr, setArr, v) {
    setArr(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
  }
  function exportCsv() {
    const headers = ["Name", "Member ID", "Classification", "Clearance Status", "Status Date", "Date Label"];
    const body = rows.map((r) => [r.name, r.member_id || "", r.classification, r.clearance_status, r.status_date || "", r.status_date_label || ""]);
    downloadCSV(`bsvac-clearance-status-${new Date().toISOString().slice(0, 10)}.csv`, toCSV(headers, body));
  }

  const pdfParams = new URLSearchParams();
  if (statusFilter.length) pdfParams.set("status", statusFilter.join(","));
  if (classFilter.length) pdfParams.set("classification", classFilter.join(","));
  const pdfQs = pdfParams.toString();

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-xs text-slate-500">{data ? `${data.count} member${data.count === 1 ? "" : "s"}` : "Loading…"}</p>
        <div className="flex items-center gap-2">
          <Input placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} className="rounded-sm w-56" data-testid="clearance-search" />
          <Button size="sm" variant="outline" className="rounded-sm" onClick={exportCsv} disabled={!rows.length} data-testid="clearance-export">
            <DownloadSimple size={12} weight="bold" className="mr-1" /> CSV
          </Button>
          <Button asChild size="sm" className="rounded-sm bg-slate-900 text-white" data-testid="clearance-pdf">
            <a href={`${API_BASE}/reports/clearance-status/pdf${pdfQs ? "?" + pdfQs : ""}`} target="_blank" rel="noreferrer">
              <FilePdf size={12} weight="bold" className="mr-1" /> PDF
            </a>
          </Button>
        </div>
      </div>

      <div className="border border-slate-200 rounded-sm p-3 bg-slate-50 space-y-2">
        <div>
          <div className="tab-tag text-slate-600 mb-1">Filter by Status</div>
          <div className="flex gap-1 flex-wrap">
            {CLEARANCE_CODES.map((c) => {
              const on = statusFilter.includes(c);
              return (
                <button
                  key={c}
                  type="button"
                  onClick={() => toggle(statusFilter, setStatusFilter, c)}
                  className={`border rounded-sm px-2 py-1 text-xs mono ${on ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 hover:bg-slate-100"}`}
                  data-testid={`clearance-filter-status-${c}`}
                >
                  {c}
                </button>
              );
            })}
            {statusFilter.length > 0 && (
              <button type="button" onClick={() => setStatusFilter([])} className="text-[11px] text-blue-700 underline ml-2">Clear</button>
            )}
          </div>
        </div>
        <div>
          <div className="tab-tag text-slate-600 mb-1">Filter by Classification</div>
          <div className="flex gap-1 flex-wrap">
            {CLASSIFICATIONS.map((c) => {
              const on = classFilter.includes(c);
              return (
                <button
                  key={c}
                  type="button"
                  onClick={() => toggle(classFilter, setClassFilter, c)}
                  className={`border rounded-sm px-2 py-1 text-xs ${on ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 hover:bg-slate-100"}`}
                  data-testid={`clearance-filter-class-${c.replace(/\s+/g, "-")}`}
                >
                  {c}
                </button>
              );
            })}
            {classFilter.length > 0 && (
              <button type="button" onClick={() => setClassFilter([])} className="text-[11px] text-blue-700 underline ml-2">Clear</button>
            )}
          </div>
        </div>
      </div>

      <div className="border border-slate-200 rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-900 text-white uppercase tab-tag">
            <tr>
              <th className="px-4 py-2 text-left">Name</th>
              <th className="px-4 py-2 text-left">Member ID</th>
              <th className="px-4 py-2 text-left">Classification</th>
              <th className="px-4 py-2 text-left">Status</th>
              <th className="px-4 py-2 text-left">Date</th>
              <th className="px-4 py-2 text-left">Date Label</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-6 text-center text-slate-500">No members match.</td></tr>
            ) : rows.map((r) => (
              <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`clearance-row-${r.id}`}>
                <td className="px-4 py-2">
                  <Link to={`/members/${r.id}`} className="font-medium hover:text-blue-700 hover:underline">{r.name}</Link>
                </td>
                <td className="px-4 py-2 mono text-xs">{r.member_id || "—"}</td>
                <td className="px-4 py-2 text-xs">{r.classification}</td>
                <td className="px-4 py-2">
                  <Badge className={`rounded-sm text-[10px] mono ${r.clearance_status === "C+" ? "bg-emerald-600 text-white" : r.clearance_status === "FTO" ? "bg-blue-600 text-white" : r.clearance_status === "C" ? "bg-slate-700 text-white" : r.clearance_status === "I" ? "bg-amber-500 text-white" : "bg-red-600 text-white"}`}>
                    {r.clearance_status}
                  </Badge>
                </td>
                <td className="px-4 py-2 mono text-xs">{r.status_date ? fmtDate(r.status_date) : "—"}</td>
                <td className="px-4 py-2 text-[11px] text-slate-600">{r.status_date_label}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ============ Dispatcher Clearance Report ============ */
const DISPATCHER_FILTERS = ["Cleared", "Not Cleared", "All"];
function DispatcherClearanceTab() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("Cleared");
  const [q, setQ] = useState("");

  async function load() {
    try {
      const r = await api.get("/reports/dispatcher-clearance", { params: { status: filter } });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load");
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const rows = useMemo(() => {
    if (!data) return [];
    const needle = q.trim().toLowerCase();
    return needle
      ? data.rows.filter((r) => (`${r.name} ${r.member_id} ${r.dispatcher_number || ""}`).toLowerCase().includes(needle))
      : data.rows;
  }, [data, q]);

  function exportCsv() {
    const headers = ["Name", "Member ID", "Status", "Cleared Date", "Dispatcher #"];
    const body = rows.map((r) => [r.name, r.member_id || "", r.dispatcher_status, r.dispatcher_clearance_date || "", r.dispatcher_number || ""]);
    downloadCSV(`bsvac-dispatcher-clearance-${new Date().toISOString().slice(0, 10)}.csv`, toCSV(headers, body));
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-xs text-slate-500">{data ? `${data.count} member${data.count === 1 ? "" : "s"}` : "Loading…"}</p>
        <div className="flex items-center gap-2">
          <Input placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} className="rounded-sm w-56" data-testid="dispatcher-search" />
          <Button size="sm" variant="outline" className="rounded-sm" onClick={exportCsv} disabled={!rows.length} data-testid="dispatcher-export">
            <DownloadSimple size={12} weight="bold" className="mr-1" /> CSV
          </Button>
          <Button asChild size="sm" className="rounded-sm bg-slate-900 text-white" data-testid="dispatcher-pdf">
            <a href={`${API_BASE}/reports/dispatcher-clearance/pdf?status=${encodeURIComponent(filter)}`} target="_blank" rel="noreferrer">
              <FilePdf size={12} weight="bold" className="mr-1" /> PDF
            </a>
          </Button>
        </div>
      </div>

      <div className="border border-slate-200 rounded-sm p-3 bg-slate-50">
        <div className="tab-tag text-slate-600 mb-1">Filter</div>
        <div className="flex gap-1 flex-wrap">
          {DISPATCHER_FILTERS.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setFilter(c)}
              className={`border rounded-sm px-3 py-1 text-xs ${filter === c ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 hover:bg-slate-100"}`}
              data-testid={`dispatcher-filter-${c.replace(/\s+/g, "-")}`}
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      <div className="border border-slate-200 rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-900 text-white uppercase tab-tag">
            <tr>
              <th className="px-4 py-2 text-left">Name</th>
              <th className="px-4 py-2 text-left">Member ID</th>
              <th className="px-4 py-2 text-left">Status</th>
              <th className="px-4 py-2 text-left">Cleared Date</th>
              <th className="px-4 py-2 text-left">Dispatcher #</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-6 text-center text-slate-500">No members match.</td></tr>
            ) : rows.map((r) => (
              <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`dispatcher-row-${r.id}`}>
                <td className="px-4 py-2">
                  <Link to={`/members/${r.id}`} className="font-medium hover:text-blue-700 hover:underline">{r.name}</Link>
                </td>
                <td className="px-4 py-2 mono text-xs">{r.member_id || "—"}</td>
                <td className="px-4 py-2">
                  <Badge className={`rounded-sm text-[10px] mono ${r.dispatcher_status === "Cleared" ? "bg-emerald-600 text-white" : r.dispatcher_status === "Not Cleared" ? "bg-red-600 text-white" : "bg-slate-400 text-white"}`}>
                    {r.dispatcher_status}
                  </Badge>
                </td>
                <td className="px-4 py-2 mono text-xs">{r.dispatcher_clearance_date ? fmtDate(r.dispatcher_clearance_date) : "—"}</td>
                <td className="px-4 py-2 mono text-xs">{r.dispatcher_number || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
